import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Building2, Phone, PhoneCall, Loader2, CheckCircle2, ShieldAlert, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { withAlpha } from "../lib/branding";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public, login-free preview page reached from the welcome email.
 * Visitors enter their phone and the system fires a one-shot AI call
 * using the client's chosen voice + branded greeting.
 */
export default function Preview() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/preview/${token}`)
      .then(({ data }) => setMeta(data))
      .catch((err) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 404) setLoadErr(detail || "This preview link isn't valid.");
        else if (status === 410) setLoadErr(detail || "This preview link has expired.");
        else setLoadErr("Couldn't load this preview link.");
      });
  }, [token]);

  const formatPhone = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    return `+${digits.slice(0, 1)} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error("Enter your full 10-digit cell number");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/preview/${token}/test-call`, { phone });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't place preview call");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Bad/expired token ───
  if (loadErr) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] text-white/90 flex items-center justify-center px-6" data-testid="preview-error">
        <div className="text-center max-w-md">
          <ShieldAlert className="w-12 h-12 text-rose-400 mx-auto mb-4" strokeWidth={1.5} />
          <div className="text-xl font-semibold mb-2">Preview unavailable</div>
          <p className="text-sm text-white/55 mb-6">{loadErr}</p>
          <p className="text-[12px] text-white/40">If your account is active, log in to your portal to keep testing.</p>
        </div>
        <Toaster theme="dark" richColors position="top-right" />
      </div>
    );
  }

  // ─── Loading ───
  if (!meta) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center text-white/40 text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const accent = meta.accent_color || "#FFB000";
  const businessName = meta.name || "your business";
  const agentName = meta.agent_name || "your AI";

  return (
    <div
      className="min-h-screen bg-[#0a0a0b] text-white/95 flex items-center justify-center relative overflow-hidden px-6"
      style={{ backgroundImage: `radial-gradient(ellipse at top, ${withAlpha(accent, 0.12)}, transparent 60%)` }}
      data-testid="preview-page"
    >
      <div
        className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full blur-3xl pointer-events-none"
        style={{ background: withAlpha(accent, 0.07) }}
      />
      <div className="w-full max-w-[520px] relative">
        {/* Branded header */}
        <div className="flex items-center gap-3 mb-12">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden border"
            style={{
              background: meta.logo_url ? "transparent" : withAlpha(accent, 0.15),
              borderColor: withAlpha(accent, 0.3),
            }}
          >
            {meta.logo_url ? (
              <img src={meta.logo_url} alt={businessName} className="w-full h-full object-contain" />
            ) : (
              <Building2 className="w-6 h-6" style={{ color: accent }} strokeWidth={1.5} />
            )}
          </div>
          <div>
            <div className="text-white text-xl font-semibold tracking-tight leading-none">{businessName}</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] mt-1.5" style={{ color: withAlpha(accent, 0.7) }}>
              AI Front Desk · Preview
            </div>
          </div>
        </div>

        {result ? (
          <div data-testid="preview-success">
            <div className="text-[10px] uppercase tracking-[0.2em] font-medium mb-3 flex items-center gap-1.5" style={{ color: accent }}>
              <Sparkles className="w-3.5 h-3.5" /> Calling you now
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight mb-3">
              Pick up to hear <span style={{ color: accent }}>{result.agent_name}</span>.
            </h1>
            <p className="text-base text-white/55 leading-relaxed mb-8">
              Your phone <strong className="text-white/85">{result.phone}</strong> should ring in a few seconds. After the call, the transcript will be waiting in your portal.
            </p>

            <Link
              to={result.portal_login_url || meta.portal_login_url}
              data-testid="preview-claim-portal-btn"
              className="block text-center font-semibold text-sm px-6 py-3.5 rounded-xl text-black transition-all"
              style={{ background: accent, boxShadow: `0 8px 24px ${withAlpha(accent, 0.3)}` }}
            >
              Claim your full portal
              <ArrowRight className="w-4 h-4 inline-block ml-2" />
            </Link>
            <p className="text-[12px] text-white/40 mt-4 text-center">
              Use the temporary password from your welcome email to log in.
            </p>

            {result.uses_remaining > 0 && (
              <button
                onClick={() => { setResult(null); setPhone(""); }}
                className="block mx-auto text-[12px] text-white/45 hover:text-white/75 mt-6"
                data-testid="preview-test-another-btn"
              >
                Test another number ({result.uses_remaining} preview{result.uses_remaining === 1 ? "" : "s"} left) →
              </button>
            )}
          </div>
        ) : meta.agent_configured ? (
          <>
            <div className="text-[10px] uppercase tracking-[0.2em] font-medium mb-3" style={{ color: accent }}>
              Hear it for yourself
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight mb-3">
              Want to hear your AI receptionist before you log in?
            </h1>
            <p className="text-base text-white/55 leading-relaxed mb-8">
              Drop your cell number below. <strong className="text-white/85">{agentName}</strong> will call you in a few seconds — the same voice that'll greet your real callers.
            </p>

            <form onSubmit={submit} className="space-y-3" data-testid="preview-form">
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30 pointer-events-none" />
                <input
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  placeholder="(555) 123-4567"
                  data-testid="preview-phone-input"
                  className="w-full bg-[#121214] border border-white/10 rounded-xl text-white text-lg pl-12 pr-4 py-4 placeholder:text-white/25 focus:outline-none transition-colors"
                  onFocus={(e) => { e.target.style.borderColor = accent; }}
                  onBlur={(e) => { e.target.style.borderColor = ""; }}
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={submitting || phone.replace(/\D/g, "").length < 10}
                data-testid="preview-submit-btn"
                className="w-full font-semibold text-base px-6 py-4 rounded-xl text-black disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
                style={{
                  background: accent,
                  boxShadow: `0 8px 24px ${withAlpha(accent, 0.3)}`,
                }}
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <PhoneCall className="w-5 h-5" />}
                {submitting ? "Calling…" : "Call me now"}
              </button>
            </form>

            <p className="text-[12px] text-white/40 mt-4 leading-relaxed">
              No login needed. We'll only use this number for the preview call. Standard call rates apply.
              {meta.uses_remaining > 0 && <> · {meta.uses_remaining} preview call{meta.uses_remaining === 1 ? "" : "s"} left on this link.</>}
            </p>

            <div className="mt-12 pt-8 border-t border-white/5 text-center">
              <p className="text-[12px] text-white/40 mb-3">Already have your password?</p>
              <Link
                to={meta.portal_login_url}
                className="text-sm font-medium hover:underline"
                style={{ color: accent }}
                data-testid="preview-portal-link"
              >
                Sign in to your full portal →
              </Link>
            </div>
          </>
        ) : (
          // Persona not yet picked
          <div data-testid="preview-not-configured">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-4" style={{ color: accent }} strokeWidth={1.5} />
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-3 text-center">
              Your AI is almost ready.
            </h1>
            <p className="text-base text-white/55 leading-relaxed text-center mb-8">
              Log in to your portal, pick a voice and a name for your AI, and we'll have it call you in seconds.
            </p>
            <Link
              to={meta.portal_login_url}
              data-testid="preview-go-to-portal-btn"
              className="block text-center font-semibold text-sm px-6 py-3.5 rounded-xl text-black"
              style={{ background: accent, boxShadow: `0 8px 24px ${withAlpha(accent, 0.3)}` }}
            >
              Open my portal <ArrowRight className="w-4 h-4 inline-block ml-2" />
            </Link>
          </div>
        )}
      </div>

      <Toaster theme="dark" richColors position="top-right" />
    </div>
  );
}
